import { useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import * as Icon from '@mui/icons-material/';
import { toast } from 'react-toastify';
import { HeaderComponent } from '../../../../shared/components/HeaderComponent';
import { Button } from '../../../Styles/ScreenStyle';
import { Loader } from '../../../../shared/components/Loader';
import UsuarioController from '../../../../controllers/UsuarioController';
import useAuth from '../../../../hooks/useAuth';

/**
 * Primeiro acesso: a pessoa cadastra a senha dela.
 *
 * Esta tela é a única que alguém em primeiro acesso consegue abrir — o menu fica
 * escondido e qualquer outra rota é devolvida para cá. É a tela que destrava o sistema,
 * então ela própria nunca pode ser barrada.
 *
 * DEPOIS DE TROCAR, DESLOGA. O token em uso ainda carrega a marca de primeiro acesso
 * assinada; continuar com ele significaria seguir preso nesta tela. Pedir um login novo é
 * mais simples e mais honesto do que inventar renovação de token — e são dois cliques.
 */
export default function TrocarSenha() {
  const auth = useAuth();
  const [nova, setNova] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  const salvar = async () => {
    setErro('');

    if (nova !== confirmacao) {
      setErro('A nova senha e a confirmação precisam ser iguais.');
      return;
    }

    // A mesma regra que a API aplica. Conferir aqui também evita uma ida ao servidor para
    // ouvir o óbvio — mas quem decide é a API, porque a tela se contorna.
    if (nova.length < 8 || !/[A-Za-zÀ-ú]/.test(nova) || !/[0-9]/.test(nova)) {
      setErro('A senha precisa ter ao menos 8 caracteres, com pelo menos uma letra e um número.');
      return;
    }

    setSalvando(true);
    const resposta = await UsuarioController.TrocarSenha(nova, confirmacao);
    setSalvando(false);

    if (resposta.StatusCode !== 200) {
      setErro(resposta?.Data?.Mensagem ?? resposta?.Data ?? 'Não foi possível alterar a senha.');
      return;
    }

    toast('Senha alterada. Entre novamente com a nova senha.', { type: 'success' });

    // Sai de verdade: encerra a sessão no servidor e limpa o token.
    await auth.logout();
    window.location.href = '/';
  };

  return (
    <Loader loading={salvando}>
      <Box>
        <HeaderComponent text={'CADASTRE SUA SENHA'} className={'mb-5'} />

        <Box className='p-3' sx={{ maxWidth: '48rem' }}>
          <Alert severity='info' sx={{ mb: 3 }}>
            Esta senha poderá ser alterada apenas neste momento. Caso seja necessário
            redefini-la futuramente, solicite a redefinição a um administrador do sistema.
          </Alert>

          <Typography variant='body2' sx={{ mb: 2, color: 'text.secondary' }}>
            Ao menos 8 caracteres, com pelo menos uma letra e um número. Ela não pode ser
            a mesma que você recebeu por e-mail.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: '35ch' }}>
            <TextField
              label='Nova senha'
              type='password'
              value={nova}
              onChange={(e) => setNova(e.target.value)}
              autoFocus
            />
            <TextField
              label='Confirmar senha'
              type='password'
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && salvar()}
            />

            {erro && <Alert severity='error'>{erro}</Alert>}

            <Button
              variant='contained'
              color='success'
              startIcon={<Icon.Save />}
              onClick={salvar}
              disabled={salvando}
            >
              Salvar e entrar
            </Button>
          </Box>
        </Box>
      </Box>
    </Loader>
  );
}
